import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import type { AppDispatch, RootState } from "@/store"
import {
  fetchEmployeesThunk,
  createEmployeeThunk,
  updateEmployeeThunk,
  deleteEmployeeThunk,
  fetchEmployeeDetailsThunk,
  acceptEmployeeThunk,
  rejectEmployeeThunk,
  flagEmployeeSectionsThunk,
  inviteEmployeeThunk,
  setSelectedEmployee,
  selectEmployees,
  selectSelectedEmployee,
  selectEmployeesLoading,
  selectEmployeesError,
  selectEmployeeDetails,
  selectEmployeeDetailsLoading,
} from "../store/employeeSlice"

export function useEmployeeService() {
  const dispatch = useDispatch<AppDispatch>()

  const employees = useSelector(selectEmployees)
  const selectedEmployee = useSelector(selectSelectedEmployee)
  const isLoading = useSelector(selectEmployeesLoading)
  const error = useSelector(selectEmployeesError)

  const loadEmployees = (searchTerm?: string) =>
    dispatch(fetchEmployeesThunk({ searchTerm })).unwrap()

  const createEmployee = async (formData: FormData) => {
    return dispatch(createEmployeeThunk(formData)).unwrap()
  }

  const updateEmployee = (args: {
    id: string
    payload: {
      firstName: string
      lastName: string
      email: string
      jobTitle: string
    }
  }) => dispatch(updateEmployeeThunk(args)).unwrap()

  const deleteEmployee = (id: string) =>
    dispatch(deleteEmployeeThunk(id)).unwrap()

  const inviteEmployee = (id: string) =>
    dispatch(inviteEmployeeThunk(id)).unwrap()

  const selectEmployee = (employeeIdOrNull: string | null) => {
    if (!employeeIdOrNull) {
      dispatch(setSelectedEmployee(null))
      return
    }

    const employee = employees.find((e) => e.id === employeeIdOrNull) ?? null
    dispatch(setSelectedEmployee(employee))
  }

  const loadEmployeeDetails = (id: string) =>
    dispatch(fetchEmployeeDetailsThunk(id)).unwrap()

  const acceptEmployee = (id: string) =>
    dispatch(acceptEmployeeThunk(id)).unwrap()

  const rejectEmployee = (id: string, notes: string) =>
    dispatch(rejectEmployeeThunk({ id, notes })).unwrap()

  const flagEmployeeSections = (
    onboardingId: string,
    flags: { fieldName: string; note: string }[],
  ) => dispatch(flagEmployeeSectionsThunk({ onboardingId, flags })).unwrap()

  return {
    employees,
    selectedEmployee,
    isLoading,
    error,
    loadEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    inviteEmployee,
    selectEmployee,
    loadEmployeeDetails,
    acceptEmployee,
    rejectEmployee,
    flagEmployeeSections,
  }
}

export function useEmployeeDetailsForm(employeeId: string) {
  const dispatch = useDispatch<AppDispatch>()
  const employeeDetails = useSelector(selectEmployeeDetails)
  const isLoading = useSelector(selectEmployeeDetailsLoading)
  const error = useSelector((state: RootState) => selectEmployeesError(state))

  const loadDetails = useCallback(() => {
    if (employeeId) {
      dispatch(fetchEmployeeDetailsThunk(employeeId))
    }
  }, [dispatch, employeeId])

  return {
    data: employeeDetails,
    isLoading,
    error,
    refetch: loadDetails,
  }
}
